import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import { entityService } from "../../services/entityService";

const log = createFileLogger('ENTITY_UNLINK');

export const entityUnlinkCommand: SelfContainedCommand = {
  id: 'entity_unlink',
  description: `Remove a typed relationship between two entities.

**Use Cases:**
- Remove Product from Website
- Unlink Task from Vehicle
- Remove Document attachment
- Break any entity relationship

**Relationship Types:**
- available_on, uses_api_connection, variant_of, references, assigned_to, etc.

**Note:** This removes the edge from entity_edges collection. Safe to call even if edge doesn't exist.`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'fromId',
        type: 'string',
        description: 'Source entity ID',
        required: true
      },
      {
        name: 'toId',
        type: 'string',
        description: 'Target entity ID',
        required: true
      },
      {
        name: 'relationType',
        type: 'string',
        description: 'Type of relationship to remove (e.g., "available_on", "uses_api_connection")',
        required: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { 
          fromId: 'product_123', 
          toId: 'website_456', 
          relationType: 'available_on' 
        },
        expectedOutput: createSuccessResult('Link removed', {
          fromId: 'product_123',
          toId: 'website_456',
          relationType: 'available_on'
        }),
        description: 'Remove product from website',
        context: 'When product should no longer appear on website'
      }
    ],
    bad: []
  },
  
  performance: {
    expectedDuration: 300,
    complexity: 'low'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { fromId, toId, relationType } = params;
    
    try {
      log.info('[entity_unlink] Removing link:', { fromId, toId, relationType });
      
      if (!fromId || !toId || !relationType) {
        return createErrorResult(
          'Missing required parameters',
          'fromId, toId, and relationType are all required',
          'validation',
          { data: { fromId, toId, relationType } }
        );
      }
      
      await entityService.removeLink(fromId, toId, relationType);
      
      log.info('[entity_unlink] ✅ Link removed successfully');
      
      return createSuccessResult(
        `Unlinked ${fromId} from ${toId}`,
        {
          fromId,
          toId,
          relationType,
          removed: true
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityService.removeLink',
            affectedEntities: [fromId]
          } as any,
          ui: {
            refreshContext: true
          }
        }
      );
      
    } catch (error: any) {
      log.error('[entity_unlink] Error:', error);
      return createErrorResult(
        'Entity unlinking failed',
        `Failed to unlink entities: ${error.message || 'Unknown error'}`,
        'execution',
        {
          data: { fromId, toId, relationType },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};
